/**
 * WebSocket singleton via context to avoid multiple connections.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './useAuth';

interface WebSocketMessage {
  type: 'opportunity' | 'price' | 'analysis' | 'alert' | 'connection';
  data?: any;
  timestamp?: string;
}

interface WebSocketContextValue {
  ws: WebSocket | null;
  messages: WebSocketMessage[];
  connected: boolean;
  latestMessages: WebSocketMessage[];
}

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);
  const [connected, setConnected] = useState(false);

  // Module-level like guards using refs
  const reconnectAttemptsRef = useRef(0);
  const connectingRef = useRef(false);

  const connect = useCallback(() => {
    if (!token) return;
    if (connectingRef.current) return;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

    connectingRef.current = true;

    const url = new URL('wss://api.finapp.aerotage.com/ws/dashboard');
    // Pass token to backend for future auth handling (backend currently accepts all)
    url.searchParams.set('token', token);

    const websocket = new WebSocket(url.toString());

    websocket.onopen = () => {
      reconnectAttemptsRef.current = 0;
      connectingRef.current = false;
      setConnected(true);
      setWs(websocket);
      // Optional heartbeat
      try {
        websocket.send('ping');
      } catch {}
    };

    websocket.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        setMessages(prev => [message, ...prev].slice(0, 200));
      } catch (error) {
        // Ignore parse errors to avoid noisy logs in production
      }
    };

    websocket.onerror = () => {
      setConnected(false);
    };

    websocket.onclose = () => {
      setConnected(false);
      connectingRef.current = false;

      // Exponential backoff up to 30s
      const attempt = Math.min(reconnectAttemptsRef.current + 1, 6);
      reconnectAttemptsRef.current = attempt;
      const delayMs = Math.min(1000 * Math.pow(2, attempt), 30000);

      window.setTimeout(() => {
        if (token) connect();
      }, delayMs);
    };
  }, [token, ws]);

  // Establish connection when token becomes available
  useEffect(() => {
    if (!token) return;
    connect();
    // Cleanup on unmount or token change
    return () => {
      try {
        ws?.close();
      } catch {}
    };
  }, [token]);

  const value = useMemo<WebSocketContextValue>(() => ({
    ws,
    messages,
    connected,
    latestMessages: messages.slice(0, 10),
  }), [ws, messages, connected]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return ctx;
}


